import { useState, type ReactNode } from 'react';
import { MAX_ASSESSMENTS, MAX_INTERVIEW_ROUNDS, type JobDetail } from '@nest-recruit/shared';
import { Card } from '../../../../components/Card';
import { ModuleGate } from '../../../../components/ModuleGate';
import { Segmented } from '../../../../components/Segmented';
import { useModule } from '../../../../lib/modules';
import { useSaveJobStep } from '../../api/jobs-api';
import { useStepAutosave } from '../../hooks/use-step-autosave';
import type { StepProps } from '../step-props';
import styles from './StageCounters.module.css';

/**
 * JB-15 as the workflow review's C4 left it (ADR 0077): two counters, so the advert is never held up.
 * Each round's panel, how it's held and the shares are set in that Interview step once the job is open;
 * a test is set up in the Assessment step. 0 tests and 0 interviews is allowed (C4-a): people go from
 * Contact straight to Selected.
 */

interface Counts {
  readonly assessments: number;
  readonly interviews: number;
}

const countsOf = (job: JobDetail): Counts => ({ assessments: job.assessment.count, interviews: job.interviewRounds.length });

const choices = (max: number) => Array.from({ length: max + 1 }, (_, count) => ({ value: String(count), label: String(count) }));

export function StageCounters({ job, isReadOnly, onConflict, footer }: StepProps): ReactNode {
  const [counts, setCounts] = useState<Counts>(() => countsOf(job));
  const isAssessmentsOn = useModule('assessments');
  const saveStep = useSaveJobStep();
  const { state, schedule, flush } = useStepAutosave<Counts>({
    job,
    onConflict,
    // Each counter is its own save; the second goes with the version the first returned.
    save: async (next, version) => {
      let saved = job;
      let current = version;
      if (isAssessmentsOn && next.assessments !== job.assessment.count) {
        saved = await saveStep.mutateAsync({ id: job.id, step: 'assessment-count', body: { version: current, count: next.assessments } });
        current = saved.version;
      }
      if (next.interviews !== job.interviewRounds.length) {
        saved = await saveStep.mutateAsync({ id: job.id, step: 'interview-count', body: { version: current, count: next.interviews } });
      }
      return saved;
    },
  });
  const lockedRounds = job.interviewRounds.filter((round) => round.isLocked).length;
  const isTestLocked = job.pipeline.stages.find((stage) => stage.stage === 'assessment')?.isLocked ?? false;
  const problems = state.kind === 'error' ? [state.message, ...Object.values(state.fields)].filter((message, index, all) => all.indexOf(message) === index) : [];

  function change(patch: Partial<Counts>): void {
    const next = { ...counts, ...patch };
    setCounts(next);
    schedule(next);
  }

  const hasNeither = counts.interviews === 0 && (!isAssessmentsOn || counts.assessments === 0);

  return (
    <Card title="Stages" meta="How many tests and interviews. Each one is set up later, once the job is open">
      <div className={styles.counters}>
        <ModuleGate module="assessments">
          <div className={styles.counter}>
            <span className={styles.label}>
              Assessments
            </span>
            <Segmented
              label="How many assessments"
              disabled={isReadOnly}
              value={String(counts.assessments)}
              options={choices(MAX_ASSESSMENTS)}
              onChange={(value) => change({ assessments: Number(value) })}
            />
          </div>
        </ModuleGate>
        <div className={styles.counter}>
          <span className={styles.label}>
            Interviews
          </span>
          <Segmented
            label="How many interviews"
            disabled={isReadOnly}
            value={String(counts.interviews)}
            options={choices(MAX_INTERVIEW_ROUNDS)}
            onChange={(value) => change({ interviews: Number(value) })}
          />
        </div>
      </div>
      {counts.interviews > 0 && (
        <p className={styles.note}>
          {Array.from({ length: counts.interviews }, (_, index) => `Round ${index + 1}`).join(', ')}. Panels and how each is held are set in the Interview step.
        </p>
      )}
      {hasNeither && <p className={styles.note}>No test and no interview: people go from Contact straight to Selected.</p>}
      {lockedRounds > 0 && <p className={styles.note}>Candidates have reached {lockedRounds === 1 ? 'Round 1' : `the first ${lockedRounds} rounds`}, so those can't be removed.</p>}
      {/* Only while it counts at least one: a job switched off before the counters can have people left in it. */}
      {isTestLocked && counts.assessments > 0 && <p className={styles.note}>Candidates have reached the Assessment, so it can't be set to 0.</p>}
      {problems.length > 0 && (
        <ul className={styles.errors} role="alert">
          {problems.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
      {footer({ state, flush })}
    </Card>
  );
}
